import { useState } from "react";
import { Tabs } from "antd";
import { capitalize } from "lodash-es";
import { useQuery } from "react-query";

import { nextTick } from "common/utils";
import { PivotTable } from "common/components/webix/react/PivotTable";
import { Placeholder } from "common/components/placeholder";
import { NoDataAvailable } from "common/helpers/errors";
import { ErrorView } from "common/components/error";
import { Qualifiers } from "common/components/queryBuilder/qualifiers";

export function TabbedPivotTable({
    className,
    section,
    pivots,
    fields,
    fieldMap,
    exportName,

    initialTabs,
    initialStructure,
    initialSection,
    initialSorting,

    queryKey,
    queryFn,
    queryDeps,
    ADD_FILTER,
    UPDATE_SECTION
}) {
    // initialize tabs with default structure on mount.
    const [tabStructureCache, setTabStructureCache] = useState(Object.fromEntries(initialTabs.map(({ id, label }) => [id, { ...initialStructure, label }])));
    const updateTabStructureCache = (obj) => setTabStructureCache({ ...tabStructureCache, ...obj });

    const activePivots = section.split(".");

    const { data, status } = useQuery([queryKey, { ...queryDeps, pivots: activePivots }], queryFn);

    const eventHandler = ({ type, data: newData }) => {
        // Don't set new props in the same stack.
        // Doing so will re-render the pivot table and then the rest of webix pt code will break with error
        nextTick(() => {
            if (type === "FIELDS_CHANGED" || type === "POPUP_APPLIED") {
                const { rows, values, columns, filters } = newData.newStructure;
                const newSection = rows.join(".");
                updateTabStructureCache({
                    [newSection]: {
                        values,
                        columns,
                        filters,
                        label: rows.map(capitalize).join(" > ")
                    }
                });
                UPDATE_SECTION(newSection);
            }
            if (type === "DRILLDOWN") {
                ADD_FILTER({
                    filter: newData.filters.map(([category, value]) => `${category}:${Qualifiers.normalizeValue(value)}`).join(" ")
                });
            }
        }, 0);
    };

    const getContent = (structure) => (
        <>
            {status === "error" && (
                <ErrorView
                    message="There was an error fetching requests"
                    custom
                    header="Error"
                />
            )}

            {status === "loading" && (
                <div className="request-details">
                    <Placeholder size="long" />
                    <Placeholder size="medium" />
                </div>
            )}

            {status === "success" && !data.length && (
                <ErrorView
                    captureInSentry={false}
                    error={new NoDataAvailable({
                        custom: true,
                        header: "No data available",
                        message: "Try expanding your date range or removing any applied filters.",
                        actions: false,
                        hideDetails: true
                    })}
                />
            )}

            {status === "success" && (
                <PivotTable
                    permissions={false}
                    getters={false}
                    chart
                    export
                    exportDefaultName={`${exportName} data by ${section}`}
                    eventHandler={eventHandler}
                    ui={{
                        fieldMap,
                        structure: {
                            rows: activePivots,
                            ...structure
                        },
                        height: 500,
                        datatable: {
                            autoheight: false
                        }
                    }}
                    pivots={pivots}
                    data={data}
                    initialSorting={initialSorting}
                    fields={fields}
                />
            )}
        </>
    );

    return (
        <Tabs
            destroyInactiveTabPane
            activeKey={section}
            onChange={UPDATE_SECTION}
            className={className}
            items={Object.entries(tabStructureCache).map(([id, structure]) => (
                {
                    label: structure.label,
                    key: id,
                    children: getContent(structure)
                }
            ))}
        />
    );
}
